// Pure helpers for the layout merges tab: what a reviewed group turns into on the wire.
//
// A manual group's choice is one object per group, kept in localStorage by useDecisions:
//
//   { accepted, survivorId, excluded: [layoutId], draft: { name, info, info2, combineHoles } }
//
// Every part is optional. No survivorId means the server's proposal, no excluded means every
// member, and no draft means the survivor is left exactly as it is.

const clean = (text) => (text == null ? '' : String(text).trim())

export const memberById = (group, layoutId) =>
  group.members.find((m) => m.layoutId === layoutId)

export const survivorIdOf = (group, choice) => {
  const picked = choice?.survivorId
  return picked != null && memberById(group, picked) ? picked : group.survivorId
}

// The editor's starting point: the survivor exactly as it stands.
export const layoutDraft = (member) => ({
  name: member.name,
  info: member.info ?? null,
  info2: member.info2 ?? null,
  combineHoles: false,
})

// Whether sending the draft would change anything. An untouched editor sends no survivor block.
export const isEdited = (draft, survivor) =>
  !!draft &&
  (!!draft.combineHoles ||
    clean(draft.name) !== clean(survivor.name) ||
    clean(draft.info) !== clean(survivor.info) ||
    clean(draft.info2) !== clean(survivor.info2))

// The layouts applying this group would hide.
export const losersOf = (group, choice) => {
  const survivorId = survivorIdOf(group, choice)
  const excluded = new Set(choice?.excluded || [])
  return group.members.filter((m) => m.layoutId !== survivorId && !excluded.has(m.layoutId))
}

// One group as `layout-merges/apply` takes it. Auto groups are sent with no choice at all.
export const decisionBody = (group, choice) => {
  const survivorId = survivorIdOf(group, choice)
  const survivor = memberById(group, survivorId)
  const body = {
    groupKey: group.groupKey,
    courseId: group.courseId,
    survivorId,
    survivorName: survivor.name,
    members: losersOf(group, choice).map((m) => ({ layoutId: m.layoutId, name: m.name })),
  }
  if (isEdited(choice?.draft, survivor)) {
    body.survivor = {
      name: choice.draft.name.trim(),
      info: clean(choice.draft.info) || null,
      info2: clean(choice.draft.info2) || null,
      combineHoles: !!choice.draft.combineHoles,
    }
  }
  return body
}

// For each hole, whether the members disagree about `field`. Drives the highlighted cells.
export const holeDisagreements = (members, field) => {
  const holeCount = Math.max(0, ...members.map((m) => m.holes.length))
  return Array.from({ length: holeCount }, (_, i) => {
    const values = members.map((m) => clean(m.holes[i]?.[field]))
    return new Set(values).size > 1
  })
}
